import { ErrorMessage, Field, Form, Formik } from "formik";
import { Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { loginWithOTP, verifyOtp } from "../../features/user/userSlice";
import { useState, useEffect, useContext } from "react";
import { CurrencyContext } from "../../features/context/CurrencyContext";
import { base_url } from "../../utils/axiosConfig";

const MemberSignIn = ({ show, onHide, setIsAuthenticated }) => {
  const [otpRequested, setOtpRequested] = useState(false);
  const [packageBuy, setPackageBuy] = useState(false);
  const [packageData, setPackageData] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const { convertPrice, getCurrencySymbol } = useContext(CurrencyContext);

  const fetchPackageData = async () => {
    try {
      const response = await fetch(`${base_url}v1/package/fetchAllpackage`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setPackageData(data.data);
    } catch (error) {
      console.error("Error fetching Package:", error);
      setError(error.message);
    }
  };

  useEffect(() => {
    if (packageBuy) {
      fetchPackageData();
    }
  }, [packageBuy]);

  const getSigninSchema = (otpRequested, packageBuy, selectedPackage) => {
    if (packageBuy && selectedPackage) {
      return Yup.object().shape({
        email: Yup.string()
          .required("Email is Required")
          .email("Email Should be valid"),
        transactionId: Yup.string().required("Transaction ID is Required"),
        transferType: Yup.string().required("Transfer Type is Required"),
        screenshot: Yup.mixed().required("Screenshot is Required"),
      });
    }
    return Yup.object().shape({
      email: Yup.string()
        .required("Email is Required")
        .email("Email Should be valid"),
      otp: Yup.string().when("otpRequested", {
        is: otpRequested,
        then: () =>
          Yup.string()
            .required("OTP is Required")
            .matches(/^\d{6}$/, "OTP must be 6 digits"),
        otherwise: () => Yup.string().notRequired(),
      }),
    });
  };

  const handleSelectPackage = (packageId, setFieldValue) => {
    setSelectedPackage(packageId);
    setFieldValue("packageId", packageId);
  };

  const handleSubmit = async (values, { resetForm }) => {
    if (!otpRequested) {
      try {
        const response = await dispatch(loginWithOTP(values.email)).unwrap();
        setOtpRequested(true);
        setPackageBuy(response.packageBuy);
        if (!response.packageBuy) {
          toast.success("OTP sent to your email.");
        }
      } catch (error) {
        toast.error("Failed to send OTP. Please try again.");
      }
    } else if (packageBuy && selectedPackage) {
      try {
        const formData = new FormData();
        formData.append("email", values.email);
        formData.append("packageId", values.packageId);
        formData.append("transactionId", values.transactionId);
        formData.append("transferType", values.transferType);
        formData.append("screenshot", values.screenshot);

        const response = await fetch(`${base_url}user/selectPackage`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        // setIsAuthenticated(true);
        resetForm();
        onHide();
        toast.success("Package selected and payment submitted successfully!");
      } catch (error) {
        toast.error("Failed to submit payment. Please try again.");
      }
    } else {
      try {
        await dispatch(
          verifyOtp({ email: values.email, otp: values.otp })
        ).unwrap();
        // setIsAuthenticated(true);
        resetForm();
        onHide();
        toast.success("Sign in successful!");
      } catch (error) {
        toast.error("Invalid OTP. Please try again.");
      }
    }
  };

  return (
    <Modal
      show={show}
      dialogClassName={
        packageBuy && !selectedPackage ? "max-w-5xl" : "max-w-lg"
      }
      onHide={onHide}
      centered
      backdrop="static"
    >
      <Modal.Body className="p-4 sm:p-6">
        <div className="relative bg-white rounded-lg">
          <button
            onClick={onHide}
            className="absolute top-2 right-2 bg-transparent border-0"
          >
            <FaTimes size={16} color="#666" />
          </button>
          <div className="text-center mb-4">
            <img
              src="/lifeon.svg"
              alt="Admin"
              className="mx-auto h-16 sm:h-20 mb-2"
            />
            <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
              Member Sign In
            </h3>
            <p className="text-gray-500 text-xs sm:text-sm">
              {packageBuy && selectedPackage
                ? "Enter Payment Details"
                : packageBuy
                ? "Select a Package"
                : "Sign in with your email and OTP"}
            </p>
          </div>
          <Formik
            initialValues={{
              email: "",
              otp: "",
              packageId: "",
              transactionId: "",
              transferType: "",
              screenshot: null,
            }}
            validationSchema={getSigninSchema(
              otpRequested,
              packageBuy,
              selectedPackage
            )}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue }) => (
              <Form className="flex flex-col gap-4">
                {!packageBuy && (
                  <div>
                    <label className="block text-sm sm:text-base font-semibold">
                      Email
                    </label>
                    <Field
                      type="email"
                      name="email"
                      placeholder="Enter Email"
                      className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100 rounded"
                      disabled={otpRequested}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                )}
                {otpRequested && !packageBuy && (
                  <div>
                    <label className="block text-sm sm:text-base font-semibold">
                      OTP
                    </label>
                    <Field
                      type="text"
                      name="otp"
                      placeholder="Enter 6-digit OTP"
                      className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100 rounded"
                    />
                    <ErrorMessage
                      name="otp"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                )}
                {packageBuy && !selectedPackage && (
                  <div className="py-4 sm:py-6 px-2 sm:px-4">
                    <div className="max-w-4xl mx-auto flex flex-col items-center">
                      <h2 className="font-['Lato'] font-semibold text-2xl sm:text-3xl text-[#303030] mb-4">
                        Packages
                      </h2>
                      <p className=" font-normal text-base sm:text-lg text-[#303030] mb-6 text-center">
                        Upgrade to premium membership with our regular packages
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        {packageData?.map((plan, index) => (
                          <div
                            key={index}
                            className="package-card bg-white p-4 rounded-lg shadow-md w-full"
                          >
                            <h3 className="font-['Lato'] font-bold text-lg sm:text-xl text-[#1A2942] mb-3 text-left">
                              {plan.name}
                            </h3>
                            <p className="font-['Lato'] font-medium text-sm sm:text-base text-[#1A2942] mb-2 text-left">
                              Start At
                            </p>
                            <div className="flex items-baseline mb-3">
                              <span className="text-xl sm:text-2xl font-bold text-[#1A2942]">
                                {getCurrencySymbol()}
                                {convertPrice(plan?.price?.amount)}
                              </span>
                              <span className="ml-2 text-sm text-gray-500">
                                /Month • {plan?.duration_days || "N/A"} days
                              </span>
                            </div>
                            <p className="font-['Lato'] font-semibold text-sm sm:text-base text-[#1A2942] mb-4 text-left">
                              {plan.description}
                            </p>
                            <div className="space-y-2 mb-6 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-[#BBFFD8] scrollbar-track-gray-200">
                              {plan?.features?.map((feature, featureIndex) => (
                                <div
                                  key={featureIndex}
                                  className="flex items-center gap-3"
                                >
                                  <div className="w-4 h-4 bg-gray-200 rounded flex items-center justify-center">
                                    <svg
                                      className="w-2 h-2"
                                      viewBox="0 0 12 12"
                                      fill="none"
                                      stroke="#434343"
                                      strokeWidth="1"
                                    >
                                      <path d="M2 6.5l3 3 5-5" />
                                    </svg>
                                  </div>
                                  <span className="font-['Lato'] font-medium text-sm text-[#1A2942] flex-1 break-words">
                                    {feature}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                handleSelectPackage(plan._id, setFieldValue)
                              }
                              className="w-full bg-[#FF8500] text-white font-['Inter'] font-semibold text-sm sm:text-base py-2 rounded-lg shadow-sm hover:bg-orange-600 transition"
                            >
                              Buy Now
                            </button>
                          </div>
                        ))}
                      </div>
                      {error && (
                        <div className="text-red-500 text-sm mt-4">{error}</div>
                      )}
                    </div>
                  </div>
                )}
                {packageBuy && selectedPackage && (
                  <div className="flex flex-col gap-4">
                    <div className="my-4 flex justify-center">
                      <div className="relative w-full max-w-xs">
                        <img
                          src="/qr.JPG"
                          alt="UPI QR Code"
                          className="w-full h-auto rounded-lg"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm sm:text-base font-semibold">
                        Transfer Type
                      </label>
                      <Field
                        as="select"
                        name="transferType"
                        className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100 rounded"
                      >
                        <option value="">Select Transfer Type</option>
                        <option value="bankTransfer">Bank Transfer</option>
                        <option value="upi">UPI</option>
                      </Field>
                      <ErrorMessage
                        name="transferType"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm sm:text-base font-semibold">
                        Transaction ID
                      </label>
                      <Field
                        type="text"
                        name="transactionId"
                        placeholder="Enter Transaction ID"
                        className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100 rounded"
                      />
                      <ErrorMessage
                        name="transactionId"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm sm:text-base font-semibold">
                        Payment Screenshot
                      </label>
                      <input
                        type="file"
                        name="screenshot"
                        accept="image/*"
                        onChange={(event) =>
                          setFieldValue(
                            "screenshot",
                            event.currentTarget.files[0]
                          )
                        }
                        className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100 rounded"
                      />
                      <ErrorMessage
                        name="screenshot"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>
                )}
                {!(packageBuy && !selectedPackage) && (
                  <button
                    type="submit"
                    className="w-full py-2 bg-gradient-to-r from-[#00C70D] to-[#005AD0] text-white rounded-lg text-sm sm:text-base font-semibold hover:opacity-90 disabled:opacity-50"
                    disabled={
                      !values.email ||
                      (otpRequested && !packageBuy && !values.otp) ||
                      (packageBuy && !selectedPackage) ||
                      (packageBuy &&
                        selectedPackage &&
                        (!values.transactionId ||
                          !values.transferType ||
                          !values.screenshot))
                    }
                  >
                    {packageBuy && selectedPackage
                      ? "Submit Payment"
                      : packageBuy
                      ? "Select a Package"
                      : otpRequested
                      ? "Verify OTP"
                      : "Request OTP"}
                  </button>
                )}
                {!packageBuy && !selectedPackage && (
                  <p className="text-center cursor-pointer text-xs sm:text-sm">
                    <span
                      className="text-[#35A93E] hover:underline"
                      onClick={() =>
                        window.open(
                          "https://lifeonplus.in/dashboardPanel/dashboard",
                          "_blank"
                        )
                      }
                    >
                      Sign In To Access Your Member Dashboard
                    </span>{" "}
                    
                  </p>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MemberSignIn;
